import { useCallback } from 'react'
import { WorkspaceFormData } from '../utils/Workspaces.utils'
import { useAtom, useAtomValue } from 'jotai'
import { riskAvailableWorkspacesAtom, riskCreateWorkspaceAtom } from '@app/state/riskAtoms'
import type { NewRiskWorkspace, RiskWorkspace } from '@app/types'
import { useMapLayers } from '@app/hooks/useMapLayers'
import { useMap } from '@app/contexts/ModuleMapContexts/RiskMapContext/RiskMapContext'
import { useSelector } from 'react-redux'
import { RootState } from '@app/redux/store'

export const useWorkspaceBuilder = () => {
  const { data: workspaces } = useAtomValue(riskAvailableWorkspacesAtom)
  const [{ mutate: createRiskWorkspace }] = useAtom(riskCreateWorkspaceAtom)

  const { elementLayers, hazardLayers, informationLayers } = useMapLayers()
  const { regionMasks } = useMap()
  const savedDrawAreas = useSelector((state: RootState) => state.riskMap.drawAreas)

  const createWorkspace = useCallback(
    async (data: WorkspaceFormData & { visibility: 'private' | 'organisation' }) => {
      if (!workspaces) return

      // Check for duplicate names
      const duplicate = workspaces.some(
        (state) => state.name.trim().toLowerCase() === data.name.trim().toLowerCase(),
      )

      if (duplicate) {
        throw new Error('A Workspace with this name already exists', { cause: 'duplicate_name' })
      }

      const createdWorkspace: NewRiskWorkspace = {
        name: data.name.trim(),
        description: data.description?.trim() || '',
        visibility: data.visibility,
        layers: {
          assets: elementLayers,
          scenarios: hazardLayers,
          contexts: informationLayers,
        },
        regions: regionMasks?.length ? regionMasks.map((r) => r.region) : null,
        drawAreas: savedDrawAreas ?? [],
      }

      const result = await new Promise<RiskWorkspace>((resolve, reject) => {
        createRiskWorkspace(createdWorkspace, {
          onSuccess: (data) => {
            resolve(data)
          },
          onError: () => {
            reject(
              new Error('An error occurred while saving the Workspace', { cause: 'save_error' }),
            )
          },
        })
      })
      return result
    },
    [
      createRiskWorkspace,
      workspaces,
      elementLayers,
      hazardLayers,
      informationLayers,
      regionMasks,
      savedDrawAreas,
    ],
  )

  return { createWorkspace }
}
